import React, { useEffect, useContext, useRef, useState, useMemo, useCallback } from 'react';
import {
    CircularProgress,
    Avatar,
    Alert,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    Chip,
    Snackbar
} from '@mui/material';
import { Mail, Search, Inbox, ArrowBack, Add, Person } from '@mui/icons-material';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { LoginContext } from '../../../contexts/LoginContext';
import useAxios from '../../../hooks/useAxios';
import MessageBubble from '../../commun/messaging/MessageBubble';
import MessageComposer from '../../commun/messaging/MessageComposer';
import LoadMoreButton from '../../commun/messaging/LoadMoreButton';
import ScrollToBottomButton from '../../commun/messaging/ScrollToBottomButton';
import { ConversationSummary } from '../../../interface/Message.interface';
import { USER_LIST_URL } from '../../../utils/constant';
import { useConversations } from '../../../api/messages/useConversations';
import { useMessages } from '../../../api/messages/useMessages';
import { useSendMessage } from '../../../api/messages/useMessageMutations';
import { queryKeys } from '../../../api/queryKeys';

interface UserOption {
    uuid: string;
    firstname: string;
    lastname: string;
    email: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const MessagingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid rgba(102, 126, 234, 0.1);

    @media (max-width: 768px) {
        padding-bottom: 0.5rem;
        flex-wrap: wrap;
        gap: 0.75rem;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.8rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const SectionIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;

    .MuiSvgIcon-root {
        color: var(--text-primary);
        font-size: 1.5rem;
    }

    @media (max-width: 768px) {
        width: 32px;
        height: 32px;
        border-radius: 8px;

        .MuiSvgIcon-root {
            font-size: 1rem;
        }
    }
`;

const StatsBar = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 0.75rem 1rem;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.3);

    @media (max-width: 768px) {
        gap: 1rem;
        padding: 0.5rem 0.75rem;
    }
`;

const StatItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .MuiSvgIcon-root {
        font-size: 1.1rem;
        color: var(--primary-color);
        opacity: 0.8;
    }
`;

const StatText = styled.span`
    font-size: 0.9rem;
    color: var(--text-secondary);
    font-weight: 500;

    strong {
        color: var(--text-primary);
        font-weight: 700;
        margin-right: 0.25rem;
    }

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const StatDivider = styled.div`
    width: 1px;
    height: 20px;
    background: rgba(102, 126, 234, 0.2);
`;

const MessagingLayout = styled.div`
    display: flex;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    height: calc(100vh - 280px);
    min-height: 400px;

    @media (max-width: 1024px) {
        height: calc(100vh - 260px);
    }

    @media (max-width: 768px) {
        height: calc(100vh - 200px);
        min-height: 300px;
        border-radius: 16px;
    }
`;

const Sidebar = styled.div<{ hideOnMobile?: boolean }>`
    width: 340px;
    min-width: 300px;
    border-right: 1px solid rgba(102, 126, 234, 0.1);
    display: flex;
    flex-direction: column;
    background: rgba(248, 250, 252, 0.5);

    @media (max-width: 1024px) {
        width: 300px;
        min-width: 260px;
    }

    @media (max-width: 768px) {
        width: 100%;
        height: 100%;
        border-right: none;
        display: ${props => props.hideOnMobile ? 'none' : 'flex'};
    }
`;

const SidebarHeader = styled.div`
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(102, 126, 234, 0.1);
    background: white;
`;

const SidebarTitle = styled.h3`
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
`;

const SearchContainer = styled.div`
    padding: 0.75rem 1rem;
    background: white;
    border-bottom: 1px solid rgba(102, 126, 234, 0.05);
`;

const NewMessageButton = styled.button`
    margin: 0.875rem;
    padding: 0.875rem 1rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.08));
    color: var(--primary-color);
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.9rem;

    &:hover {
        background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.35);
    }

    &:active {
        transform: translateY(0);
    }
`;

const UserSelectList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
`;

const UserSelectItem = styled.div<{ selected?: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 0.875rem 1rem;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 0.5rem;
    background: ${props => props.selected
        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.1))'
        : 'white'};
    border: 1px solid ${props => props.selected
        ? 'rgba(102, 126, 234, 0.3)'
        : 'rgba(102, 126, 234, 0.1)'};

    &:hover {
        background: ${props => props.selected
            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.15))'
            : 'rgba(102, 126, 234, 0.05)'};
        transform: translateX(4px);
    }
`;

const UserSelectInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const UserSelectName = styled.span`
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text-primary);
    display: block;
`;

const UserSelectEmail = styled.span`
    font-size: 0.8rem;
    color: var(--text-secondary);
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ConversationsList = styled.div`
    flex: 1;
    overflow-y: auto;
`;

const ConversationItem = styled.div<{ active?: boolean; unread?: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    border-bottom: 1px solid rgba(102, 126, 234, 0.05);
    position: relative;
    background: ${props => {
        if (props.active) return 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.1))';
        if (props.unread) return 'rgba(102, 126, 234, 0.04)';
        return 'transparent';
    }};

    ${props => props.unread && !props.active && `
        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 3px;
            height: 60%;
            background: linear-gradient(180deg, var(--primary-color), var(--secondary-color));
            border-radius: 0 4px 4px 0;
        }
    `}

    &:hover {
        background: ${props => props.active
            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.18), rgba(118, 75, 162, 0.12))'
            : 'rgba(102, 126, 234, 0.08)'};
        transform: translateX(4px);
    }

    &:last-child {
        border-bottom: none;
    }
`;

const UserAvatar = styled(Avatar)`
    width: 48px !important;
    height: 48px !important;
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color)) !important;
    font-weight: 600 !important;
    font-size: 1rem !important;
    flex-shrink: 0;
    box-shadow: 0 3px 10px rgba(102, 126, 234, 0.25);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    ${ConversationItem}:hover & {
        transform: scale(1.05);
        box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
    }

    @media (max-width: 768px) {
        width: 40px !important;
        height: 40px !important;
        font-size: 0.875rem !important;
    }
`;

const ConversationContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const ConversationTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.125rem;
`;

const UserName = styled.span<{ unread?: boolean }>`
    font-weight: ${props => props.unread ? '700' : '600'};
    color: var(--text-primary);
    font-size: 0.95rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ConversationTime = styled.span<{ unread?: boolean }>`
    font-size: 0.7rem;
    color: ${props => props.unread ? 'var(--primary-color)' : 'var(--text-secondary)'};
    font-weight: ${props => props.unread ? '600' : '500'};
    flex-shrink: 0;
    margin-left: 0.5rem;
`;

const UserEmail = styled.span`
    font-size: 0.75rem;
    color: var(--text-secondary);
    display: block;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MessagePreview = styled.p<{ unread?: boolean }>`
    font-size: 0.85rem;
    color: ${props => props.unread ? 'var(--text-primary)' : 'var(--text-secondary)'};
    font-weight: ${props => props.unread ? '500' : '400'};
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ProjectChip = styled(Chip)`
    &.MuiChip-root {
        background: rgba(102, 126, 234, 0.1);
        color: var(--primary-color);
        border: 1px solid rgba(102, 126, 234, 0.3);
        font-weight: 500;
        font-size: 0.65rem;
        height: 20px;
        margin-top: 0.375rem;
    }
`;

const UnreadBadge = styled.span`
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    color: white;
    font-weight: 700;
    font-size: 0.7rem;
    min-width: 22px;
    height: 22px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 7px;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
    animation: badgePulse 2s ease-in-out infinite;

    @keyframes badgePulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
    }
`;

const MainPanel = styled.div<{ showOnMobile?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: rgba(248, 250, 252, 0.3);
    position: relative;

    @media (max-width: 768px) {
        display: ${props => props.showOnMobile ? 'flex' : 'none'};
        width: 100%;
        height: 100%;
    }
`;

const ThreadHeader = styled.div`
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(102, 126, 234, 0.1);
    display: flex;
    align-items: center;
    gap: 1rem;
    background: white;
    flex-shrink: 0;

    @media (max-width: 768px) {
        padding: 0.875rem 1rem;
        gap: 0.75rem;
    }
`;

const BackButton = styled.button`
    display: none;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border: none;
    border-radius: 10px;
    background: rgba(102, 126, 234, 0.1);
    color: var(--primary-color);
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: rgba(102, 126, 234, 0.2);
    }

    &:active {
        transform: scale(0.97);
    }

    @media (max-width: 768px) {
        display: flex;
    }
`;

const ThreadUserInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const ThreadUserName = styled.h3`
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
`;

const ThreadUserEmail = styled.span`
    font-size: 0.8rem;
    color: var(--text-secondary);
`;

const MessageThread = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.5) 0%, rgba(255, 255, 255, 0.3) 100%);

    @media (max-width: 768px) {
        padding: 1rem;
        gap: 0.75rem;
    }
`;

const DateSeparator = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 0.5rem 0;
    padding: 0 1rem;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.2), transparent);
    }
`;

const DateLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.375rem 1rem;
    background: rgba(102, 126, 234, 0.08);
    border-radius: 20px;
    white-space: nowrap;
`;

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
    text-align: center;
    padding: 3rem 2rem;
    background: linear-gradient(180deg, transparent 0%, rgba(102, 126, 234, 0.02) 100%);
`;

const EmptyStateIcon = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.1));
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    position: relative;
    animation: floatIcon 3s ease-in-out infinite;

    &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 2px solid rgba(102, 126, 234, 0.2);
        animation: pulseRing 2s ease-out infinite;
    }

    @keyframes floatIcon {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-6px); }
    }

    @keyframes pulseRing {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(1.3); opacity: 0; }
    }

    .MuiSvgIcon-root {
        font-size: 2.25rem;
        color: var(--primary-color);
        opacity: 0.8;
    }
`;

const EmptyStateTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 0.75rem 0;
    background: linear-gradient(135deg, var(--text-primary), var(--primary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const EmptyStateDescription = styled.p`
    font-size: 0.95rem;
    color: var(--text-secondary);
    margin: 0;
    max-width: 300px;
    line-height: 1.6;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
`;

// ============================================================================
// Component
// ============================================================================

const AdminMessagingView: React.FC = () => {
    // React Query hooks
    const { data: conversationsData, isLoading: loadingConversations, error: conversationsError, refetch: refetchConversations } = useConversations();
    const queryClient = useQueryClient();
    const sendMessageMutation = useSendMessage();

    // Local state
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isComposingNew, setIsComposingNew] = useState(false);
    const [availableUsers, setAvailableUsers] = useState<UserOption[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Fetch messages for current conversation
    const { data: messagesData, isLoading: loadingMessages } = useMessages(currentConversationId);

    const { currentUser } = useContext(LoginContext);
    const { getWithAxios } = useAxios();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messageThreadRef = useRef<HTMLDivElement>(null);
    const prevConversationId = useRef<string | null>(null);
    const prevMessagesCount = useRef<number>(0);

    const conversations = conversationsData?.conversations || [];
    const currentMessages = messagesData?.messages || [];
    const error = conversationsError ? (conversationsError as Error).message : null;

    // Scroll to bottom when conversation changes or new message
    useEffect(() => {
        const isNewConversation = prevConversationId.current !== currentConversationId;
        const hasNewMessage = currentMessages.length > prevMessagesCount.current && prevMessagesCount.current > 0;

        if ((isNewConversation || hasNewMessage) && messageThreadRef.current) {
            messageThreadRef.current.scrollTop = messageThreadRef.current.scrollHeight;
        }

        prevConversationId.current = currentConversationId;
        prevMessagesCount.current = currentMessages.length;
    }, [currentConversationId, currentMessages.length]);

    // Load users when composing new message
    useEffect(() => {
        const loadUsers = async () => {
            if (!isComposingNew || availableUsers.length > 0) return;

            setLoadingUsers(true);
            try {
                const response = await getWithAxios(USER_LIST_URL);
                const users = Array.isArray(response.data) ? response.data : [];
                setAvailableUsers(users);
            } catch (err) {
                console.error('Erreur chargement utilisateurs:', err);
            } finally {
                setLoadingUsers(false);
            }
        };
        loadUsers();
    }, [isComposingNew, availableUsers.length, getWithAxios]);

    const handleSelectConversation = useCallback((conversation: ConversationSummary) => {
        setIsComposingNew(false);
        setSelectedUser(null);
        setCurrentConversationId(conversation.uuid);
    }, []);

    const handleNewMessage = useCallback(() => {
        setCurrentConversationId(null);
        setSelectedUser(null);
        setUserSearchQuery('');
        setIsComposingNew(true);
    }, []);

    const handleSelectUser = useCallback((user: UserOption) => {
        setSelectedUser(user);
    }, []);

    const handleCancelNewMessage = useCallback(() => {
        setIsComposingNew(false);
        setSelectedUser(null);
        setUserSearchQuery('');
    }, []);

    const clearCurrentConversation = useCallback(() => {
        setCurrentConversationId(null);
    }, []);

    const handleSendMessage = useCallback(async (content: string): Promise<boolean> => {
        // Composing new message
        if (isComposingNew && selectedUser) {
            try {
                await sendMessageMutation.mutateAsync({
                    recipientId: selectedUser.uuid,
                    content,
                });
                setIsComposingNew(false);
                setSelectedUser(null);
                setSnackbarOpen(true);
                await refetchConversations();
                return true;
            } catch {
                return false;
            }
        }

        // Reply in existing conversation
        const selectedConversation = conversations.find(c => c.uuid === currentConversationId);
        if (!selectedConversation) return false;

        try {
            await sendMessageMutation.mutateAsync({
                recipientId: selectedConversation.participantUuid,
                content,
                projectUuid: selectedConversation.projectUuid,
            });
            setSnackbarOpen(true);
            // Invalidate messages for this conversation
            queryClient.invalidateQueries({ queryKey: queryKeys.messages.conversation(currentConversationId || '') });
            await refetchConversations();
            return true;
        } catch {
            return false;
        }
    }, [isComposingNew, selectedUser, conversations, currentConversationId, sendMessageMutation, refetchConversations, queryClient]);

    const handleScroll = useCallback(() => {
        if (messageThreadRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = messageThreadRef.current;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
            setShowScrollButton(!isNearBottom);
        }
    }, []);

    const scrollToBottom = useCallback(() => {
        if (messageThreadRef.current) {
            messageThreadRef.current.scrollTo({
                top: messageThreadRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, []);

    const getInitials = (name: string): string => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Filter users by search
    const filteredUsers = availableUsers.filter(user => {
        if (!userSearchQuery) return true;
        const query = userSearchQuery.toLowerCase();
        return (
            user.firstname.toLowerCase().includes(query) ||
            user.lastname.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query)
        );
    });

    // Filter conversations by search
    const filteredConversations = conversations.filter(conv => {
        if (!searchQuery) return true;
        return (
            conv.participantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            conv.participantEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (conv.projectName?.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    });

    const selectedConversation = conversations.find(c => c.uuid === currentConversationId);
    const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);
    const totalConversations = conversations.length;

    // Group messages by date
    const messagesWithDateGroups = useMemo(() => {
        const result: Array<{ type: 'date' | 'message'; content: string | typeof currentMessages[0] }> = [];
        let lastDateGroup = '';

        currentMessages.forEach((message) => {
            const dateGroup = message.getDateGroup();
            if (dateGroup !== lastDateGroup) {
                result.push({ type: 'date', content: dateGroup });
                lastDateGroup = dateGroup;
            }
            result.push({ type: 'message', content: message });
        });

        return result;
    }, [currentMessages]);

    return (
        <MessagingContainer>
            <SectionHeader>
                <HeaderLeft>
                    <SectionIcon>
                        <Mail />
                    </SectionIcon>
                    <SectionTitle>Messagerie</SectionTitle>
                </HeaderLeft>
            </SectionHeader>

            <StatsBar>
                <StatItem>
                    <Inbox />
                    <StatText><strong>{totalConversations}</strong> Conversations</StatText>
                </StatItem>
                <StatDivider />
                <StatItem>
                    <Mail />
                    <StatText><strong>{totalUnread}</strong> Non lus</StatText>
                </StatItem>
            </StatsBar>

            {error && (
                <Alert severity="error" sx={{ borderRadius: '12px' }}>
                    {error}
                </Alert>
            )}

            <MessagingLayout>
                <Sidebar hideOnMobile={!!currentConversationId || isComposingNew}>
                    <SidebarHeader>
                        <SidebarTitle>Messages</SidebarTitle>
                    </SidebarHeader>

                    <SearchContainer>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Rechercher..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(248, 250, 252, 0.8)',
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                    </SearchContainer>

                    <NewMessageButton onClick={handleNewMessage}>
                        <Add fontSize="small" />
                        Nouveau message
                    </NewMessageButton>

                    <ConversationsList>
                        {loadingConversations && conversations.length === 0 ? (
                            <LoadingContainer>
                                <CircularProgress size={24} />
                            </LoadingContainer>
                        ) : filteredConversations.length === 0 ? (
                            <EmptyState style={{ padding: '2rem', minHeight: 'auto' }}>
                                <EmptyStateIcon style={{ width: 56, height: 56 }}>
                                    <Inbox style={{ fontSize: '1.5rem' }} />
                                </EmptyStateIcon>
                                <EmptyStateTitle style={{ fontSize: '1rem' }}>
                                    {searchQuery ? 'Aucun resultat' : 'Aucune conversation'}
                                </EmptyStateTitle>
                                <EmptyStateDescription style={{ fontSize: '0.85rem' }}>
                                    {searchQuery
                                        ? 'Essayez avec d\'autres termes.'
                                        : 'Les messages des utilisateurs apparaitront ici.'
                                    }
                                </EmptyStateDescription>
                            </EmptyState>
                        ) : (
                            <>
                                {filteredConversations.map((conversation) => (
                                    <ConversationItem
                                        key={conversation.uuid}
                                        active={currentConversationId === conversation.uuid}
                                        unread={conversation.unreadCount > 0}
                                        onClick={() => handleSelectConversation(conversation)}
                                    >
                                        <UserAvatar>
                                            {getInitials(conversation.participantName)}
                                        </UserAvatar>
                                        <ConversationContent>
                                            <ConversationTop>
                                                <UserName unread={conversation.unreadCount > 0}>
                                                    {conversation.participantName}
                                                </UserName>
                                                <ConversationTime unread={conversation.unreadCount > 0}>
                                                    {conversation.formattedLastMessageAt}
                                                </ConversationTime>
                                            </ConversationTop>
                                            <UserEmail>{conversation.participantEmail}</UserEmail>
                                            <MessagePreview unread={conversation.unreadCount > 0}>
                                                {conversation.lastMessage}
                                            </MessagePreview>
                                            {conversation.projectName && (
                                                <ProjectChip
                                                    label={conversation.projectName}
                                                    size="small"
                                                />
                                            )}
                                        </ConversationContent>
                                        {conversation.unreadCount > 0 && (
                                            <UnreadBadge>
                                                {conversation.unreadCount}
                                            </UnreadBadge>
                                        )}
                                    </ConversationItem>
                                ))}
                            </>
                        )}
                    </ConversationsList>
                </Sidebar>

                <MainPanel showOnMobile={!!currentConversationId || isComposingNew}>
                    {currentConversationId && selectedConversation ? (
                        <>
                            <ThreadHeader>
                                <BackButton onClick={clearCurrentConversation}>
                                    <ArrowBack fontSize="small" />
                                    Retour
                                </BackButton>
                                <UserAvatar style={{ width: 40, height: 40 }}>
                                    {getInitials(selectedConversation.participantName)}
                                </UserAvatar>
                                <ThreadUserInfo>
                                    <ThreadUserName>
                                        {selectedConversation.participantName}
                                    </ThreadUserName>
                                    <ThreadUserEmail>
                                        {selectedConversation.participantEmail}
                                    </ThreadUserEmail>
                                </ThreadUserInfo>
                                {selectedConversation.projectName && (
                                    <ProjectChip
                                        label={selectedConversation.projectName}
                                        size="small"
                                        style={{ marginTop: 0 }}
                                    />
                                )}
                            </ThreadHeader>

                            <MessageThread ref={messageThreadRef} onScroll={handleScroll}>
                                {loadingMessages ? (
                                    <LoadingContainer>
                                        <CircularProgress size={24} />
                                    </LoadingContainer>
                                ) : currentMessages.length === 0 ? (
                                    <EmptyState>
                                        <EmptyStateIcon>
                                            <Mail />
                                        </EmptyStateIcon>
                                        <EmptyStateTitle>Aucun message</EmptyStateTitle>
                                        <EmptyStateDescription>
                                            Cette conversation ne contient pas encore de messages.
                                        </EmptyStateDescription>
                                    </EmptyState>
                                ) : (
                                    <>
                                        {messagesWithDateGroups.map((item, index) => (
                                            item.type === 'date' ? (
                                                <DateSeparator key={`date-${index}`}>
                                                    <DateLabel>{item.content as string}</DateLabel>
                                                </DateSeparator>
                                            ) : (
                                                <MessageBubble
                                                    key={(item.content as typeof currentMessages[0]).uuid}
                                                    message={item.content as typeof currentMessages[0]}
                                                    isOwn={(item.content as typeof currentMessages[0]).isSentBy(currentUser?.uuid || '')}
                                                />
                                            )
                                        ))}
                                    </>
                                )}
                                <div ref={messagesEndRef} />
                            </MessageThread>
                            <ScrollToBottomButton visible={showScrollButton} onClick={scrollToBottom} />

                            <MessageComposer
                                onSend={handleSendMessage}
                                disabled={loadingMessages}
                                placeholder="Repondre..."
                            />
                        </>
                    ) : isComposingNew ? (
                        <>
                            <ThreadHeader>
                                <BackButton onClick={handleCancelNewMessage} style={{ display: 'flex' }}>
                                    <ArrowBack fontSize="small" />
                                    Annuler
                                </BackButton>
                                <ThreadUserInfo>
                                    <ThreadUserName>
                                        {selectedUser
                                            ? `Message à ${selectedUser.firstname} ${selectedUser.lastname}`
                                            : 'Nouveau message'
                                        }
                                    </ThreadUserName>
                                    {selectedUser && (
                                        <ThreadUserEmail>{selectedUser.email}</ThreadUserEmail>
                                    )}
                                </ThreadUserInfo>
                            </ThreadHeader>

                            {!selectedUser ? (
                                <>
                                    <SearchContainer>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Rechercher un utilisateur..."
                                            value={userSearchQuery}
                                            onChange={(e) => setUserSearchQuery(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search fontSize="small" />
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    backgroundColor: 'rgba(248, 250, 252, 0.8)',
                                                    fontSize: '0.875rem'
                                                }
                                            }}
                                        />
                                    </SearchContainer>
                                    <UserSelectList>
                                        {loadingUsers ? (
                                            <LoadingContainer>
                                                <CircularProgress size={24} />
                                            </LoadingContainer>
                                        ) : filteredUsers.length === 0 ? (
                                            <EmptyState style={{ padding: '2rem', minHeight: 'auto' }}>
                                                <EmptyStateIcon style={{ width: 56, height: 56 }}>
                                                    <Person style={{ fontSize: '1.5rem' }} />
                                                </EmptyStateIcon>
                                                <EmptyStateDescription style={{ fontSize: '0.9rem' }}>
                                                    {userSearchQuery ? 'Aucun utilisateur trouve' : 'Aucun utilisateur disponible'}
                                                </EmptyStateDescription>
                                            </EmptyState>
                                        ) : (
                                            filteredUsers.map((user) => (
                                                <UserSelectItem
                                                    key={user.uuid}
                                                    onClick={() => handleSelectUser(user)}
                                                >
                                                    <UserAvatar style={{ width: 40, height: 40, fontSize: '0.85rem' }}>
                                                        {getInitials(`${user.firstname} ${user.lastname}`)}
                                                    </UserAvatar>
                                                    <UserSelectInfo>
                                                        <UserSelectName>
                                                            {user.firstname} {user.lastname}
                                                        </UserSelectName>
                                                        <UserSelectEmail>{user.email}</UserSelectEmail>
                                                    </UserSelectInfo>
                                                </UserSelectItem>
                                            ))
                                        )}
                                    </UserSelectList>
                                </>
                            ) : (
                                <>
                                    <MessageThread style={{ flex: 1 }}>
                                        <EmptyState style={{ padding: '2rem' }}>
                                            <EmptyStateIcon>
                                                <Mail />
                                            </EmptyStateIcon>
                                            <EmptyStateTitle>
                                                Envoyer un message
                                            </EmptyStateTitle>
                                            <EmptyStateDescription>
                                                Redigez votre message pour {selectedUser.firstname} {selectedUser.lastname}
                                            </EmptyStateDescription>
                                        </EmptyState>
                                    </MessageThread>
                                    <MessageComposer
                                        onSend={handleSendMessage}
                                        disabled={loadingMessages}
                                        placeholder={`Message à ${selectedUser.firstname}...`}
                                    />
                                </>
                            )}
                        </>
                    ) : (
                        <EmptyState>
                            <EmptyStateIcon>
                                <Mail />
                            </EmptyStateIcon>
                            <EmptyStateTitle>
                                Selectionnez une conversation
                            </EmptyStateTitle>
                            <EmptyStateDescription>
                                Choisissez une conversation dans la liste ou creez un nouveau message.
                            </EmptyStateDescription>
                        </EmptyState>
                    )}
                </MainPanel>
            </MessagingLayout>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message="Message envoye"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </MessagingContainer>
    );
};

export default AdminMessagingView;
